import { useState, useEffect } from "react";
import { getPendingFlights, getAllPendingFlights, getEmailHistory, deleteEmailHistoryItem, deleteAllEmailHistory, approvePendingFlight, rejectPendingFlight, deletePendingFlight, deleteAllPendingFlights, submitManualEmail, uploadPdfForParsing, pollEmailsNow } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Mail, Check, X, Clock, ChevronDown, ChevronUp, 
  Plane, Send, FileText, AlertCircle, Eye, History, Trash2, Upload, RefreshCw,
  Maximize2, Minimize2, CheckCheck
} from "lucide-react";

const FLIGHT_TYPES = ["Passenger", "Private", "Cargo", "Positioning", "Ambulance", "State", "Military"];

const FlightFieldEditor = ({ data, onChange }) => {
  const update = (field, value) => onChange({ ...data, [field]: value });

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 text-sm">
      <div>
        <label className="text-gray-400 text-xs block mb-1">Flight Date</label>
        <Input type="date" value={data.flight_date || ""} onChange={e => update("flight_date", e.target.value)} className="bg-[#0f172a] border-gray-600 text-white h-8" data-testid="pf-flight-date" />
      </div>
      <div>
        <label className="text-gray-400 text-xs block mb-1">Flight Number</label>
        <Input value={data.flight_number || ""} onChange={e => update("flight_number", e.target.value)} className="bg-[#0f172a] border-gray-600 text-white h-8" data-testid="pf-flight-number" />
      </div>
      <div>
        <label className="text-gray-400 text-xs block mb-1">Registration</label>
        <Input value={data.registration || ""} onChange={e => update("registration", e.target.value.toUpperCase())} className="bg-[#0f172a] border-gray-600 text-white h-8" data-testid="pf-registration" />
      </div>
      <div>
        <label className="text-gray-400 text-xs block mb-1">Time (STA/STD)</label>
        <Input type="time" value={data.time || ""} onChange={e => update("time", e.target.value)} className="bg-[#0f172a] border-gray-600 text-white h-8" data-testid="pf-time" />
      </div>
      <div>
        <label className="text-gray-400 text-xs block mb-1">Origin (ICAO)</label>
        <Input value={data.origin || ""} onChange={e => update("origin", e.target.value.toUpperCase())} maxLength={4} className="bg-[#0f172a] border-gray-600 text-white h-8" data-testid="pf-origin" />
      </div>
      <div>
        <label className="text-gray-400 text-xs block mb-1">Destination (ICAO)</label>
        <Input value={data.destination || ""} onChange={e => update("destination", e.target.value.toUpperCase())} maxLength={4} className="bg-[#0f172a] border-gray-600 text-white h-8" data-testid="pf-destination" />
      </div>
      <div>
        <label className="text-gray-400 text-xs block mb-1">Aircraft Type</label>
        <Input value={data.aircraft_type || ""} onChange={e => update("aircraft_type", e.target.value)} className="bg-[#0f172a] border-gray-600 text-white h-8" data-testid="pf-aircraft-type" />
      </div>
      <div>
        <label className="text-gray-400 text-xs block mb-1">Flight Type</label>
        <select value={data.flight_type || "Passenger"} onChange={e => update("flight_type", e.target.value)} className="w-full bg-[#0f172a] border border-gray-600 text-white rounded-md h-8 px-2 text-sm" data-testid="pf-flight-type">
          {FLIGHT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>
      <div>
        <label className="text-gray-400 text-xs block mb-1">Pilot in Command</label>
        <Input value={data.pilot_in_command || ""} onChange={e => update("pilot_in_command", e.target.value)} className="bg-[#0f172a] border-gray-600 text-white h-8" data-testid="pf-pilot" />
      </div>
      <div>
        <label className="text-gray-400 text-xs block mb-1">Crew</label>
        <Input type="number" value={data.crew_number || 0} onChange={e => update("crew_number", parseInt(e.target.value) || 0)} className="bg-[#0f172a] border-gray-600 text-white h-8" data-testid="pf-crew" />
      </div>
      <div>
        <label className="text-gray-400 text-xs block mb-1">PAX In</label>
        <Input type="number" value={data.pax_in || 0} onChange={e => update("pax_in", parseInt(e.target.value) || 0)} className="bg-[#0f172a] border-gray-600 text-white h-8" data-testid="pf-pax-in" />
      </div>
      <div>
        <label className="text-gray-400 text-xs block mb-1">PAX Out</label>
        <Input type="number" value={data.pax_out || 0} onChange={e => update("pax_out", parseInt(e.target.value) || 0)} className="bg-[#0f172a] border-gray-600 text-white h-8" data-testid="pf-pax-out" />
      </div>
      <div>
        <label className="text-gray-400 text-xs block mb-1">MTOW (kg)</label>
        <Input type="number" value={data.mtow || 0} onChange={e => update("mtow", parseFloat(e.target.value) || 0)} className="bg-[#0f172a] border-gray-600 text-white h-8" data-testid="pf-mtow" />
      </div>
      <div>
        <label className="text-gray-400 text-xs block mb-1">Operator</label>
        <Input value={data.operator || ""} onChange={e => update("operator", e.target.value)} className="bg-[#0f172a] border-gray-600 text-white h-8" data-testid="pf-operator" />
      </div>
      <div className="col-span-2">
        <label className="text-gray-400 text-xs block mb-1">Notes</label>
        <Input value={data.notes || ""} onChange={e => update("notes", e.target.value)} className="bg-[#0f172a] border-gray-600 text-white h-8" data-testid="pf-notes" />
      </div>
    </div>
  );
};

const PendingFlightCard = ({ flight, onApprove, onReject, onDelete, isMasterAdmin, forceExpanded }) => {
  const [localExpanded, setLocalExpanded] = useState(false);
  const expanded = forceExpanded !== null && forceExpanded !== undefined ? forceExpanded : localExpanded;
  const [editData, setEditData] = useState({ ...(flight.parsed_data || {}), notes: "" });
  const [showEmail, setShowEmail] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  const statusColors = {
    pending: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
    approved: "bg-green-500/20 text-green-400 border-green-500/30",
    rejected: "bg-red-500/20 text-red-400 border-red-500/30"
  };

  return (
    <div className="bg-[#0f172a] border border-gray-700 rounded-lg overflow-hidden" data-testid={`pending-flight-${flight.id}`}>
      {/* Header */}
      <div 
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-[#1e293b] transition-colors"
        onClick={() => setLocalExpanded(!expanded)}
      >
        <div className="flex items-center gap-4">
          <Mail size={18} className="text-blue-400" />
          <div>
            <p className="text-white font-medium text-sm">{flight.email_subject}</p>
            <p className="text-gray-400 text-xs">From: {flight.email_sender} | {new Date(flight.created_at).toLocaleString()}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className={`px-2 py-0.5 rounded text-xs border ${statusColors[flight.status]}`}>
            {flight.status.toUpperCase()}
          </span>
          {editData.registration && (
            <span className="text-[#d4af37] font-mono text-sm">{editData.registration}</span>
          )}
          {isMasterAdmin && (
            <button
              onClick={e => { e.stopPropagation(); onDelete(flight.id); }}
              className="text-gray-600 hover:text-red-400 transition-colors p-1"
              data-testid={`delete-request-${flight.id}`}
              title="Delete request"
            >
              <Trash2 size={14} />
            </button>
          )}
          {expanded ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
        </div>
      </div>

      {/* Expanded Content */}
      {expanded && (
        <div className="border-t border-gray-700 p-4 space-y-4">
          {/* Email Preview */}
          {flight.email_body && (
            <div>
              <button onClick={() => setShowEmail(!showEmail)} className="flex items-center gap-2 text-blue-400 hover:text-blue-300 text-xs mb-2">
                <Eye size={14} /> {showEmail ? "Hide" : "Show"} Original Email
              </button>
              {showEmail && (
                <div className="bg-[#020617] border border-gray-700 rounded p-3 text-gray-300 text-xs max-h-40 overflow-y-auto whitespace-pre-wrap">
                  {flight.email_body}
                </div>
              )}
            </div>
          )}

          {/* Editable Fields */}
          {flight.status === "pending" ? (
            <>
              <FlightFieldEditor data={editData} onChange={setEditData} />
              
              <div className="flex items-center gap-3 pt-2 border-t border-gray-700">
                <Button 
                  onClick={() => onApprove(flight.id, editData)}
                  className="bg-green-600 hover:bg-green-700 text-white"
                  data-testid={`approve-${flight.id}`}
                >
                  <Check size={16} className="mr-1" /> Approve & Create Flight
                </Button>
                
                {!rejecting ? (
                  <Button 
                    variant="outline"
                    onClick={() => setRejecting(true)}
                    className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                    data-testid={`reject-btn-${flight.id}`}
                  >
                    <X size={16} className="mr-1" /> Reject
                  </Button>
                ) : (
                  <div className="flex items-center gap-2 flex-1">
                    <Input 
                      value={rejectReason} 
                      onChange={e => setRejectReason(e.target.value)}
                      placeholder="Reason for rejection..." 
                      className="bg-[#020617] border-red-500/30 text-white h-8 flex-1"
                      data-testid={`reject-reason-${flight.id}`}
                    />
                    <Button size="sm" onClick={() => { onReject(flight.id, rejectReason); setRejecting(false); }} className="bg-red-600 hover:bg-red-700 text-white" data-testid={`reject-confirm-${flight.id}`}>
                      Confirm
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setRejecting(false)} className="text-gray-400">Cancel</Button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="text-sm text-gray-400">
              {flight.status === "approved" && <p>Approved by {flight.reviewed_by} on {new Date(flight.reviewed_at).toLocaleString()}</p>}
              {flight.status === "rejected" && <p>Rejected by {flight.reviewed_by}: {flight.reject_reason || "No reason"}</p>}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const HistoryRow = ({ email, isMasterAdmin, onDelete }) => {
  const [expanded, setExpanded] = useState(false);
  const typeColors = {
    flight: "text-blue-400 bg-blue-500/10 border-blue-500/30",
    lvo: "text-yellow-400 bg-yellow-500/10 border-yellow-500/30",
    other: "text-gray-400 bg-gray-500/10 border-gray-500/30"
  };
  const typeLabels = { flight: "Flight", lvo: "LVO", other: "Other" };

  return (
    <div className="border-b border-gray-800 last:border-0">
      <div 
        className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-[#1e293b]/50 transition-colors"
        onClick={() => setExpanded(!expanded)}
        data-testid={`history-row-${email.id}`}
      >
        <span className={`px-2 py-0.5 rounded text-xs border whitespace-nowrap ${typeColors[email.type] || typeColors.other}`}>
          {typeLabels[email.type] || "Other"}
        </span>
        <span className="text-white text-sm truncate flex-1">{email.subject}</span>
        <span className="text-gray-500 text-xs whitespace-nowrap">{email.sender?.split('<')[0]?.trim()}</span>
        <span className="text-gray-600 text-xs whitespace-nowrap">{new Date(email.processed_at).toLocaleString()}</span>
        {isMasterAdmin && (
          <button 
            onClick={e => { e.stopPropagation(); onDelete(email.id); }}
            className="text-gray-600 hover:text-red-400 transition-colors p-1"
            data-testid={`delete-history-${email.id}`}
            title="Delete"
          >
            <Trash2 size={14} />
          </button>
        )}
        {expanded ? <ChevronUp size={14} className="text-gray-500" /> : <ChevronDown size={14} className="text-gray-500" />}
      </div>
      {expanded && (
        <div className="px-4 pb-3 space-y-2">
          <div className="bg-[#020617] border border-gray-700 rounded p-3">
            <p className="text-[#d4af37] text-sm font-medium mb-1">{email.summary}</p>
            <p className="text-gray-500 text-xs">From: {email.sender}</p>
            {email.date && <p className="text-gray-500 text-xs">Date: {email.date}</p>}
          </div>
          {email.body && (
            <div className="bg-[#020617] border border-gray-700 rounded p-3 text-gray-400 text-xs max-h-48 overflow-y-auto whitespace-pre-wrap">
              {email.body}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default function EmailPendingPage() {
  const { user } = useAuth();
  const isMasterAdmin = user?.role === "master_admin";
  const [pending, setPending] = useState([]);
  const [allFlights, setAllFlights] = useState([]);
  const [emailHistory, setEmailHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("pending");
  const [showManual, setShowManual] = useState(false);
  const [manualEmail, setManualEmail] = useState({ subject: "", body: "", sender: "" });
  const [submitting, setSubmitting] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [polling, setPolling] = useState(false);
  const [expandAll, setExpandAll] = useState(null);
  const [approvingAll, setApprovingAll] = useState(false);

  const loadData = async () => {
    try {
      setLoading(true);
      const [pendingRes, allRes, historyRes] = await Promise.all([
        getPendingFlights(),
        getAllPendingFlights(),
        getEmailHistory()
      ]);
      setPending(pendingRes.data);
      setAllFlights(allRes.data);
      setEmailHistory(historyRes.data);
    } catch (err) {
      toast.error("Failed to load pending flights");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const handleApprove = async (id, flightData) => {
    try {
      await approvePendingFlight(id, flightData);
      toast.success("Flight created successfully!");
      loadData();
    } catch (err) {
      if (err.response?.status === 409) {
        toast.warning(err.response.data.detail || "Already handled by another user");
        loadData();
      } else {
        toast.error("Failed to approve flight");
      }
    }
  };

  const handleReject = async (id, reason) => {
    try {
      await rejectPendingFlight(id, reason);
      toast.success("Flight request rejected");
      loadData();
    } catch (err) {
      if (err.response?.status === 409) {
        toast.warning(err.response.data.detail || "Already handled by another user");
        loadData();
      } else {
        toast.error("Failed to reject flight");
      }
    }
  };

  const handleDeleteRequest = async (id) => {
    try {
      await deletePendingFlight(id);
      toast.success("Request deleted");
      loadData();
    } catch (err) {
      toast.error("Failed to delete request");
    }
  };

  const handleDeleteAllRequests = async () => {
    if (!window.confirm("Delete all flight requests?")) return;
    try {
      await deleteAllPendingFlights();
      toast.success("All requests deleted");
      loadData();
    } catch (err) {
      toast.error("Failed to delete requests");
    }
  };

  const handleManualSubmit = async () => {
    if (!manualEmail.subject && !manualEmail.body) {
      toast.error("Please enter email subject and body");
      return;
    }
    try {
      setSubmitting(true);
      const res = await submitManualEmail(manualEmail);
      if (res.data.type === "flight") {
        toast.success(`${res.data.parsed_count} flight(s) parsed from email`);
      } else if (res.data.type === "lvo") {
        toast.success("LVO notification detected and created!");
      } else {
        toast.info("Email not recognized as flight or LVO");
      }
      setManualEmail({ subject: "", body: "", sender: "" });
      setShowManual(false);
      loadData();
    } catch (err) {
      toast.error("Failed to process email");
    } finally {
      setSubmitting(false);
    }
  };

  const displayList = tab === "pending" ? pending : allFlights;

  const handlePdfUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Only PDF files are supported");
      return;
    }
    try {
      setUploadingPdf(true);
      const res = await uploadPdfForParsing(file);
      if (res.data.type === "flight") {
        toast.success(`${res.data.parsed_count} flight(s) parsed from PDF`);
      } else if (res.data.type === "lvo") {
        toast.success("LVO notification detected in PDF!");
      } else {
        toast.info("No flight data found in PDF");
      }
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to process PDF");
    } finally {
      setUploadingPdf(false);
      e.target.value = "";
    }
  };

  const handleDeleteHistory = async (id) => {
    try {
      await deleteEmailHistoryItem(id);
      setEmailHistory(prev => prev.filter(e => e.id !== id));
      toast.success("History entry deleted");
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const handleDeleteAllHistory = async () => {
    if (!window.confirm("Delete all email history?")) return;
    try {
      await deleteAllEmailHistory();
      setEmailHistory([]);
      toast.success("All history deleted");
    } catch (err) {
      toast.error("Failed to delete history");
    }
  };

  const handlePullMail = async () => {
    setPolling(true);
    try {
      const res = await pollEmailsNow();
      const d = res.data;
      toast.success(`Mail check complete: ${d.processed || 0} emails processed, ${d.flights || 0} flights, ${d.lvo || 0} LVO`);
      loadData();
    } catch (err) {
      toast.error("Failed to pull emails");
    } finally {
      setPolling(false);
    }
  };

  const handleApproveAll = async () => {
    if (pending.length === 0) return;
    if (!window.confirm(`Approve all ${pending.length} pending flight(s) using their parsed data?`)) return;
    setApprovingAll(true);
    let success = 0, conflicts = 0, failed = 0;
    for (const flight of pending) {
      try {
        await approvePendingFlight(flight.id, flight.parsed_data || {});
        success += 1;
      } catch (err) {
        if (err.response?.status === 409) conflicts += 1;
        else failed += 1;
      }
    }
    const msg = `${success} approved` + (conflicts ? `, ${conflicts} already handled` : "") + (failed ? `, ${failed} failed` : "");
    if (failed > 0) toast.error(msg);
    else toast.success(msg);
    setApprovingAll(false);
    loadData();
  };

  return (
    <div className="space-y-6" data-testid="email-pending-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white">Email Flight Requests</h1>
          <p className="text-gray-400 text-sm mt-1">Review and approve flights parsed from incoming emails</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            onClick={handlePullMail}
            disabled={polling}
            variant="outline"
            className="border-green-500/50 text-green-400 hover:bg-green-500/10"
            data-testid="pull-mail-btn"
          >
            <RefreshCw size={16} className={`mr-2 ${polling ? "animate-spin" : ""}`} />
            {polling ? "Checking..." : "Pull Mail"}
          </Button>
          <label className="cursor-pointer">
            <input type="file" accept=".pdf" onChange={handlePdfUpload} className="hidden" data-testid="pdf-upload-input" />
            <div className={`inline-flex items-center px-4 py-2 rounded-md text-sm font-medium border transition-colors ${uploadingPdf ? "border-gray-600 text-gray-500" : "border-orange-500/50 text-orange-400 hover:bg-orange-500/10"}`}>
              <Upload size={16} className={`mr-2 ${uploadingPdf ? "animate-spin" : ""}`} />
              {uploadingPdf ? "Parsing PDF..." : "Upload PDF"}
            </div>
          </label>
          <Button 
            onClick={() => setShowManual(!showManual)}
            variant="outline"
            className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
            data-testid="manual-email-btn"
          >
            <Send size={16} className="mr-2" /> Submit Email Manually
          </Button>
        </div>
      </div>

      {/* Manual Email Submission */}
      {showManual && (
        <div className="bg-[#0f172a] border border-blue-500/30 rounded-lg p-4 space-y-3" data-testid="manual-email-form">
          <h3 className="text-white font-medium flex items-center gap-2"><FileText size={16} /> Manual Email Submission</h3>
          <p className="text-gray-400 text-xs">Paste email content here. AI will parse it for flight information or LVO notifications.</p>
          <Input 
            value={manualEmail.sender}
            onChange={e => setManualEmail(prev => ({ ...prev, sender: e.target.value }))}
            placeholder="Sender email (optional)"
            className="bg-[#020617] border-gray-600 text-white"
            data-testid="manual-email-sender"
          />
          <Input 
            value={manualEmail.subject}
            onChange={e => setManualEmail(prev => ({ ...prev, subject: e.target.value }))}
            placeholder="Email subject"
            className="bg-[#020617] border-gray-600 text-white"
            data-testid="manual-email-subject"
          />
          <textarea 
            value={manualEmail.body}
            onChange={e => setManualEmail(prev => ({ ...prev, body: e.target.value }))}
            placeholder="Paste the email body here..."
            rows={6}
            className="w-full bg-[#020617] border border-gray-600 text-white rounded-md p-3 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-blue-500"
            data-testid="manual-email-body"
          />
          <div className="flex gap-2">
            <Button onClick={handleManualSubmit} disabled={submitting} className="bg-blue-600 hover:bg-blue-700" data-testid="manual-email-submit">
              {submitting ? "Parsing with AI..." : "Parse & Submit"}
            </Button>
            <Button variant="ghost" onClick={() => setShowManual(false)} className="text-gray-400">Cancel</Button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-[#0f172a] rounded-lg p-1 w-fit">
        <button 
          onClick={() => setTab("pending")}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${tab === "pending" ? "bg-[#d4af37] text-[#0f172a]" : "text-gray-400 hover:text-white"}`}
          data-testid="tab-pending"
        >
          <Clock size={14} className="inline mr-1" /> Pending ({pending.length})
        </button>
        <button 
          onClick={() => setTab("all")}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${tab === "all" ? "bg-[#d4af37] text-[#0f172a]" : "text-gray-400 hover:text-white"}`}
          data-testid="tab-all"
        >
          <FileText size={14} className="inline mr-1" /> All Requests ({allFlights.length})
        </button>
        {tab === "all" && isMasterAdmin && allFlights.length > 0 && (
          <button 
            onClick={handleDeleteAllRequests}
            className="px-3 py-2 rounded-md text-sm font-medium text-red-400 hover:bg-red-500/10 transition-colors"
            data-testid="delete-all-requests"
          >
            <Trash2 size={14} className="inline mr-1" /> Delete All
          </button>
        )}
        <button 
          onClick={() => setTab("history")}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${tab === "history" ? "bg-[#d4af37] text-[#0f172a]" : "text-gray-400 hover:text-white"}`}
          data-testid="tab-history"
        >
          <History size={14} className="inline mr-1" /> Mail History ({emailHistory.length})
        </button>
        {tab === "history" && isMasterAdmin && emailHistory.length > 0 && (
          <button 
            onClick={handleDeleteAllHistory}
            className="px-3 py-2 rounded-md text-sm font-medium text-red-400 hover:bg-red-500/10 transition-colors ml-2"
            data-testid="delete-all-history"
          >
            <Trash2 size={14} className="inline mr-1" /> Delete All
          </button>
        )}
      </div>

      {/* Bulk actions for Pending tab */}
      {tab === "pending" && !loading && pending.length > 0 && (
        <div className="flex items-center justify-between bg-[#0f172a] border border-gray-700 rounded-lg px-4 py-3" data-testid="pending-bulk-actions">
          <p className="text-sm text-gray-400">{pending.length} pending request{pending.length !== 1 ? "s" : ""}</p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setExpandAll(expandAll === true ? false : true)}
              className="border-gray-600 text-gray-300 hover:bg-gray-700"
              data-testid="expand-all-pending"
            >
              {expandAll === true ? (
                <><Minimize2 size={14} className="mr-2" /> Collapse All</>
              ) : (
                <><Maximize2 size={14} className="mr-2" /> Review All</>
              )}
            </Button>
            <Button
              size="sm"
              onClick={handleApproveAll}
              disabled={approvingAll}
              className="bg-green-600 hover:bg-green-700 text-white"
              data-testid="approve-all-pending"
            >
              <CheckCheck size={14} className="mr-2" />
              {approvingAll ? "Approving..." : `Approve All (${pending.length})`}
            </Button>
          </div>
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-[#d4af37] border-t-transparent"></div>
        </div>
      ) : tab === "history" ? (
        emailHistory.length === 0 ? (
          <div className="text-center py-16 bg-[#0f172a] rounded-lg border border-gray-700">
            <History size={48} className="mx-auto text-gray-600 mb-4" />
            <p className="text-gray-400 text-lg">No email history yet</p>
            <p className="text-gray-500 text-sm mt-1">Emails processed via IMAP or manual submission will appear here</p>
          </div>
        ) : (
          <div className="bg-[#0f172a] border border-gray-700 rounded-lg overflow-hidden" data-testid="email-history-list">
            {emailHistory.map(email => (
              <HistoryRow key={email.id} email={email} isMasterAdmin={isMasterAdmin} onDelete={handleDeleteHistory} />
            ))}
          </div>
        )
      ) : displayList.length === 0 ? (
        <div className="text-center py-16 bg-[#0f172a] rounded-lg border border-gray-700">
          <Mail size={48} className="mx-auto text-gray-600 mb-4" />
          <p className="text-gray-400 text-lg">No {tab === "pending" ? "pending" : ""} flight requests</p>
          <p className="text-gray-500 text-sm mt-1">
            {tab === "pending" ? "Submit emails manually or configure IMAP to auto-detect flights" : "No flight requests have been processed yet"}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {displayList.map(flight => (
            <PendingFlightCard
              key={flight.id}
              flight={flight}
              onApprove={handleApprove}
              onReject={handleReject}
              onDelete={handleDeleteRequest}
              isMasterAdmin={isMasterAdmin}
              forceExpanded={tab === "pending" ? expandAll : null}
            />
          ))}
        </div>
      )}
    </div>
  );
}
